/**
 * help-prose — prose blocks for the in-app guides (AUTONOMY · Layer 3).
 *
 * Guides are written as TSX components with lingui `msg` macros (one
 * string per paragraph/step) so the content goes through the normal
 * i18n pipeline (extract → catalogs → `t()`), with no external markdown or
 * duplicated strings. These helpers keep every guide declarative and visually
 * consistent.
 */
import { For, type JSX, Show } from "solid-js";
import type { MessageDescriptor } from "@lingui/core";
import { ArrowRight } from "@fleet/icons";

import { useI18n } from "@/providers/i18n-provider";
import { Link } from "@tanstack/solid-router";

/** Guide paragraph. */
export function P(props: { msg: MessageDescriptor }): JSX.Element {
  const { t } = useI18n();
  return <p class="text-sm leading-relaxed text-muted-foreground">{t(props.msg)}</p>;
}

/** Numbered steps list ("how to do X" tasks). */
export function Steps(props: { items: MessageDescriptor[] }): JSX.Element {
  const { t } = useI18n();
  return (
    <ol class="list-decimal space-y-1.5 pl-5 text-sm leading-relaxed text-muted-foreground marker:font-semibold marker:text-primary">
      <For each={props.items}>{(item) => <li>{t(item)}</li>}</For>
    </ol>
  );
}

/** Bullet list (concepts, a registry's options…). */
export function Bullets(props: { items: MessageDescriptor[] }): JSX.Element {
  const { t } = useI18n();
  return (
    <ul class="list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-muted-foreground marker:text-primary">
      <For each={props.items}>{(item) => <li>{t(item)}</li>}</For>
    </ul>
  );
}

/**
 * Deep-link to the real screen where the described task is done. Every guide must
 * take the reader to the app, not describe it in the abstract.
 */
export function GoTo(props: { to: string; label: MessageDescriptor }): JSX.Element {
  const { t } = useI18n();
  return (
    <Link
      to={props.to}
      class="inline-flex w-fit items-center gap-1.5 rounded-xl border border-primary/30 bg-primary/5 px-3 py-1.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/10"
      data-testid="help-goto"
    >
      {t(props.label)}
      <ArrowRight class="h-3.5 w-3.5" />
    </Link>
  );
}

/** Highlighted note (warnings, permission nuances, offline behavior). */
export function Note(props: { msg: MessageDescriptor; title?: MessageDescriptor }): JSX.Element {
  const { t } = useI18n();
  return (
    <div class="rounded-xl border border-accent/30 bg-accent/5 p-3 text-sm leading-relaxed text-muted-foreground">
      <Show when={props.title}>
        {(title) => <span class="mr-1 font-semibold text-foreground">{t(title())}: </span>}
      </Show>
      {t(props.msg)}
    </div>
  );
}
